// import type { EditorOptions } from "@tiptap/core";
import { Blockquote } from "@tiptap/extension-blockquote";
import { Bold } from "@tiptap/extension-bold";
import { BulletList } from "@tiptap/extension-bullet-list";
import { Code } from "@tiptap/extension-code";
import { CodeBlock } from "@tiptap/extension-code-block";
import { Color } from "@tiptap/extension-color";
import { Document } from "@tiptap/extension-document";
import { Dropcursor } from "@tiptap/extension-dropcursor";
import { FontFamily } from "@tiptap/extension-font-family";
import { Gapcursor } from "@tiptap/extension-gapcursor";
import { HardBreak } from "@tiptap/extension-hard-break";
import { Highlight } from "@tiptap/extension-highlight";
import { History } from "@tiptap/extension-history";
import { HorizontalRule } from "@tiptap/extension-horizontal-rule";
import { Italic } from "@tiptap/extension-italic";
import { Link } from "@tiptap/extension-link";
import { ListItem } from "@tiptap/extension-list-item";
import { Mention } from "@tiptap/extension-mention";
import { OrderedList } from "@tiptap/extension-ordered-list";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Strike } from "@tiptap/extension-strike";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
import { TaskItem } from "@tiptap/extension-task-item";
import { TaskList } from "@tiptap/extension-task-list";
import { Text } from "@tiptap/extension-text";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Underline } from "@tiptap/extension-underline";
import { BackgroundColor } from "./BackgroundColor";
import { InlineFields } from "./InlineFields";
import HHHeading from "./HHHeading";
import HHHeadingInline from "./HHHeadingInline";
import BackgroundColorContext from "../../context/BackgroundColorContext";

import SelectionHighlight from "./comments/HighlightPlugin";
import TrackChanges from "./comments/TrackChanges";

import { useMemo, useContext } from "react";
import {
  FontSize,
  HeadingWithAnchor,
  LinkBubbleMenuHandler,
  ResizableImage,
  TableImproved,
} from "mui-tiptap";

const CustomLinkExtension = Link.extend({
  inclusive: false,
});

const CustomSubscript = Subscript.extend({
  excludes: "superscript",
});

const CustomSuperscript = Superscript.extend({
  excludes: "subscript",
});

const styles = `
  .selection-highlight {
    background-color: #faf594;
    border-radius: 2px;
  }
  
  .add-comment-button {
    position: absolute;
    transform: translate(6px, -50%);
    border: none;
    background: #ffffff;
    color: #1a1a1a;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    border-radius: 4px;
    width: 28px;
    height: 28px;
    padding: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.1s ease-in-out;
    border: 1px solid #e2e2e2;
  }
  
  .add-comment-button:hover {
    background: #f5f5f5;
    box-shadow: 0 2px 6px rgba(0,0,0,0.15);
    transform: translate(6px, -50%) scale(1.05);
  }
  
  .add-comment-button:active {
    background: #eeeeee;
    transform: translate(6px, -50%) scale(0.95);
  }
  
  .add-comment-button > div {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export default function useExtensions({
  placeholder,
  isEditable,
  elementId,
  onEditorInlineFieldsChange,
  existingInlineFields,
  elementProps,
  isBorder,
  comments,
  trackChangesComments,
  isTrackingEnabled,
  trackChangeDisplayBy,
  highlightContext,
  handleSave,
  isTrackingHistoryEnabled,
  trackHistorys,
  isHighlightPluginEnabled
} = {}) {
  console.log("useExtensions initialized");
  console.log(comments);
  console.log(trackChangesComments);
  const { backgroundColor } = useContext(BackgroundColorContext);
  return useMemo(() => {
    console.log("useExtensions - useMemo executed");
    console.log(trackChangesComments);
    return [
      TableImproved.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,

      BulletList,
      CodeBlock,
      Document,
      HardBreak,
      ListItem,
      OrderedList,
      Paragraph,
      CustomSubscript,
      CustomSuperscript,
      Text,

      Bold,
      Blockquote,

      Code,
      Italic,
      Underline,
      Strike,
      CustomLinkExtension.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: false,
      }),
      LinkBubbleMenuHandler,

      Gapcursor,
      HeadingWithAnchor.configure({
        levels: [1, 2, 3, 4, 5, 6],
      }),
      HHHeading.configure({
        HTMLAttributes: {
          style: `background-color: ${backgroundColor}; font-size: 18px;`,
        },
      }),
      HHHeadingInline.configure({
        HTMLAttributes: {
          style: `background-color: ${backgroundColor}; font-size: 18px;`,
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph", "image", "hhHeading", "hhHeadingInline"],
      }),
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      Highlight.configure({ multicolor: true }),
      HorizontalRule,

      ResizableImage,
      Dropcursor,

      TaskList,
      TaskItem.configure({
        nested: true,
      }),

      Placeholder.configure({
        placeholder,
      }),

      BackgroundColor,

      InlineFields.configure({
        editable: isEditable,
        elementId: elementId,
        onEditorInlineFieldsChange: onEditorInlineFieldsChange,
        inlineFields: existingInlineFields,
        elementProps: elementProps,
        isBorder: isBorder,
      }),

      History,

      // Selection highlight system
      SelectionHighlight.configure({
        comments: comments,
        trackChangeDisplayBy: trackChangeDisplayBy,
        highlightContext: highlightContext,
        handleSave,
        isHighlightPluginEnabled,
      }),
      TrackChanges.configure({
        trackChangesComments: trackChangesComments,
        isTrackingEnabled: isTrackingEnabled,
        trackChangeDisplayBy: trackChangeDisplayBy,
        handleSave,
        isTrackingHistoryEnabled,
        trackHistorys: trackHistorys,
        onChangeAccept: (change) => {
          // Custom logic when a change is accepted
          console.log("Change accepted:", change);
        },
        onChangeReject: (change) => {
          // Custom logic when a change is rejected
          console.log("Change rejected:", change);
        },
      }),
    ];
  }, [
    placeholder,
    isEditable,
    elementId,
    onEditorInlineFieldsChange,
    existingInlineFields,
    elementProps,
    isBorder,
    comments,
    trackChangesComments,
    isTrackingEnabled,
    handleSave,
    isTrackingHistoryEnabled,
    trackHistorys,
    isHighlightPluginEnabled,
  ]);
}
